import { Group, Tag } from '../../../apis/api-client';
import { PagedTableColumn } from '../../common/PagedTable';
import { MoreVertIcon, PersonIcon } from '../../icons';
import { Box, Chip, IconButton, Tooltip, Typography } from '../../ui';
import { formatDateTime } from '../../../utils/roleHelpers';

interface BuildGroupColumnsArgs {
  locked: boolean;
  tagById: Record<number, Tag>;
  onOpenRowMenu: (anchorEl: HTMLElement, row: Group) => void;
}

/**
 * Column definitions for the GroupsDirectory table. The column sets
 * diverge by mode: the locked workspace view shows the workspace-scoped
 * admin-count + content-tag enrichments; the account-wide view shows
 * owner + workspace instead (no single workspace to resolve admin/tags
 * against). Pure config — the enrichment maps and the row-menu opener are
 * supplied by the orchestrator so the renderers stay presentational.
 */
export function buildGroupColumns({
  locked,
  tagById,
  onOpenRowMenu,
}: BuildGroupColumnsArgs): PagedTableColumn<Group>[] {
  const nameColumn: PagedTableColumn<Group> = {
    key: 'name',
    label: 'Group',
    sortable: true,
    render: (g) => (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {g.name || 'Untitled group'}
        </Typography>
        {g.description && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {g.description}
          </Typography>
        )}
      </Box>
    ),
  };

  const actionsColumn: PagedTableColumn<Group> = {
    key: 'actions',
    label: 'Actions',
    width: 80,
    align: 'right',
    // Single kebab affordance. Row click navigates to the group editor;
    // the menu carries Edit / Assign / Delete so admins can act without
    // leaving the table.
    render: (g) => (
      <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
        <Tooltip title="Actions">
          <IconButton
            size="small"
            onClick={(e) => onOpenRowMenu(e.currentTarget, g)}
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  };

  const lockedColumns: PagedTableColumn<Group>[] = [
    nameColumn,
    {
      key: 'users',
      label: 'Users',
      width: 110,
      align: 'center',
      sortable: true,
      // Users = total members minus admins. Both counts ride on the group
      // search row (server-computed), so the value is exact on first paint.
      render: (g) => {
        const total = g.memberCount ?? 0;
        const users = Math.max(0, total - (g.adminCount ?? 0));
        return (
          <Typography variant="body2" fontWeight={600}>
            {users.toLocaleString()}
          </Typography>
        );
      },
    },
    {
      key: 'admins',
      label: 'Admins',
      width: 110,
      align: 'center',
      sortable: true,
      // Admin headcount rides on the group search row (server-computed).
      render: (g) => (
        <Typography variant="body2" fontWeight={600}>
          {(g.adminCount ?? 0).toLocaleString()}
        </Typography>
      ),
    },
    {
      key: 'tags',
      label: 'Content tags',
      align: 'center',
      // Three states: inherits-from-workspace pill (default), em-dash
      // (custom but none picked), or tag chips with a +N overflow.
      render: (g) => {
        if (g.customizeContent !== true) {
          return (
            <Tooltip title="This group inherits the workspace's content tags. Open the group to override.">
              <Chip
                label="Inherits from workspace"
                size="small"
                sx={{
                  height: 22,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  bgcolor: 'grey.100',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  '& .MuiChip-label': { px: 0.9 },
                }}
              />
            </Tooltip>
          );
        }
        const ids = g.contentTagIds ?? [];
        if (ids.length === 0) {
          return (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          );
        }
        const resolved = ids.map((tid) => tagById[tid]).filter((t): t is Tag => !!t);
        if (resolved.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              {ids.length} tag{ids.length === 1 ? '' : 's'}
            </Typography>
          );
        }
        const VISIBLE = 4;
        const shown = resolved.slice(0, VISIBLE);
        const overflow = resolved.length - shown.length;
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
            {shown.map((t) => (
              <Chip
                key={t.id}
                label={t.name}
                size="small"
                sx={{
                  height: 22,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  bgcolor: 'rgba(var(--brand-rgb), 0.16)',
                  color: 'primary.main',
                  '& .MuiChip-label': { px: 0.9 },
                }}
              />
            ))}
            {overflow > 0 && (
              <Tooltip title={resolved.slice(VISIBLE).map((t) => t.name).join(', ')}>
                <Chip
                  label={`+${overflow}`}
                  size="small"
                  sx={{
                    height: 22,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    bgcolor: 'grey.100',
                    color: 'text.secondary',
                    '& .MuiChip-label': { px: 0.9 },
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    actionsColumn,
  ];

  const accountWideColumns: PagedTableColumn<Group>[] = [
    nameColumn,
    {
      key: 'members',
      label: 'Members',
      width: 120,
      align: 'center',
      sortable: true,
      render: (g) => <Chip label={`${g.memberCount || 0}`} size="small" />,
    },
    {
      key: 'owner',
      label: 'Owner',
      render: (g) =>
        g.ownerName ? (
          <Chip label={g.ownerName} size="small" icon={<PersonIcon />} />
        ) : (
          <Typography color="text.secondary">—</Typography>
        ),
    },
    {
      key: 'workspace',
      label: 'Workspace',
      render: (g) => g.workspaceName || '—',
    },
    {
      key: 'created',
      label: 'Created',
      render: (g) => formatDateTime(g.createdAt),
    },
    actionsColumn,
  ];

  return locked ? lockedColumns : accountWideColumns;
}
